import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Trash2, X, FileBarChart, Loader2, CheckCircle2, XCircle,
  Eye, Building2, Banknote, Edit2
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

// ── Progress Claim v2 model — Quantity is Single Source of Truth ──
// Primary inputs:  contract_qty, quantity_executed, previous_qty, unit_price
// Derived:         contract_amount, total_previous, current_qty, total_current, pct
const emptyItem = () => ({
  description: '', unit: 'ls',
  contract_qty: 0, unit_price: 0,
  quantity_executed: 0, previous_qty: 0,
  // Derived (kept in state for display; source of truth is inputs above)
  contract_amount: 0, total_previous: 0, current_qty: 0, total_current: 0, pct: 0,
});

// Back-derive v2 quantity fields from legacy monetary v1 records so
// editing an old claim still works. If unit_price is 0 we can't back-derive,
// so we fall back to zeroes and let the user re-enter.
const migrateLegacyItem = (raw) => {
  if (!raw) return emptyItem();
  const it = { ...emptyItem(), ...raw };
  const up = Number(it.unit_price || 0);
  if ((!it.quantity_executed && !it.previous_qty) && up > 0) {
    const legacyPrev = Number(raw.previous_amount || 0);
    const legacyCur  = Number(raw.cumulative_amount || 0);
    if (legacyPrev || legacyCur) {
      it.previous_qty      = Number((legacyPrev / up).toFixed(6));
      it.quantity_executed = Math.max(0, Number(((legacyCur - legacyPrev) / up).toFixed(6)));
    }
  }
  return it;
};

const emptyForm = () => ({
  project_id: '',
  claim_type: 'client',
  subcontractor_id: '',
  claim_date: new Date().toISOString().slice(0, 10),
  claim_period: '',
  items: [emptyItem()],
  retention_rate: 5,
  advance_recovery_rate: 0,
  advance_recovery_amount: 0,
  vat_rate: 15,
  notes: '',
  cost_center_id: '',
});

const computeTotalsClient = (form) => {
  const rateAR = Number(form.advance_recovery_rate || 0) / 100;

  let sumTotalCurrent = 0;
  let sumTotalPrevious = 0;
  let sumAdvanceRec = 0;

  form.items.forEach((it) => {
    const cq = Number(it.contract_qty || 0);
    const up = Number(it.unit_price || 0);
    const qe = Number(it.quantity_executed || 0);
    const pq = Number(it.previous_qty || 0);
    const currentQty     = pq + qe;
    const contractAmount = cq * up;
    const totalCurrent   = currentQty * up;

    sumTotalCurrent  += totalCurrent;
    sumTotalPrevious += pq * up;

    // Per-item Advance Recovery: cap at Contract Amount once complete/over-run.
    if (cq > 0 && currentQty >= cq) {
      sumAdvanceRec += contractAmount * rateAR;
    } else {
      sumAdvanceRec += totalCurrent * rateAR;
    }
  });

  const cumulative_work_done = sumTotalCurrent;
  const previous_claimed     = sumTotalPrevious;
  const current_gross = Math.max(0, cumulative_work_done - previous_claimed);
  const retention = current_gross * (Number(form.retention_rate || 0) / 100);
  const override = Number(form.advance_recovery_amount || 0);
  const advance_rec = override > 0 ? override : sumAdvanceRec;

  // v2 VAT rule (2026-07-04): VAT applies to the net billable base:
  //     VAT = max(0, Current Gross − Advance Recovery) × VAT%
  const vatBase = Math.max(0, current_gross - advance_rec);
  const vat = vatBase * (Number(form.vat_rate || 0) / 100);

  const total_payable = current_gross - retention - advance_rec + vat;
  return {
    cumulative_work_done,
    previous_claimed,
    current_gross,
    retention_amount: retention,
    advance_recovery_amount: advance_rec,
    vat_amount: vat,
    total_payable,
  };
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    paid: 'bg-blue-100 text-blue-700',
    draft: 'bg-gray-100 text-gray-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded ${map[status] || 'bg-gray-100'}`}>{status}</span>;
};

const ProgressClaims = ({ user }) => {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('client');

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('claim_type', filterType);
      const [claimsRes, projRes, supRes, ccRes] = await Promise.all([
        axiosInstance.get(`/progress-claims${params.toString() ? `?${params}` : ''}`),
        axiosInstance.get('/projects'),
        axiosInstance.get('/suppliers'),
        axiosInstance.get('/cost-centers').catch(() => ({ data: [] })),
      ]);
      setRows(claimsRes.data);
      setProjects(projRes.data);
      setSuppliers(supRes.data);
      setCostCenters(ccRes.data);
    } catch (e) {
      toast.error(`فشل التحميل: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setForm(emptyForm()); setEditingId(null); setShowModal(true); };
  const openEdit = (row) => {
    setForm({
      project_id: row.project_id || '',
      claim_type: row.claim_type || 'client',
      subcontractor_id: row.subcontractor_id || '',
      claim_date: row.claim_date || new Date().toISOString().slice(0, 10),
      claim_period: row.claim_period || '',
      items: row.items?.length ? row.items.map(migrateLegacyItem) : [emptyItem()],
      retention_rate: row.retention_rate ?? 5,
      advance_recovery_rate: row.advance_recovery_rate ?? 0,
      advance_recovery_amount: row.advance_recovery_amount ?? 0,
      vat_rate: row.vat_rate ?? 15,
      notes: row.notes || '',
      cost_center_id: row.cost_center_id || '',
    });
    setEditingId(row.id);
    setShowModal(true);
  };

  const updateItem = (idx, patch) => {
    setForm((prev) => {
      const items = [...prev.items];
      const it = { ...items[idx], ...patch };
      const cq = Number(it.contract_qty || 0);
      const up = Number(it.unit_price || 0);
      const qe = Number(it.quantity_executed || 0);
      const pq = Number(it.previous_qty || 0);
      it.contract_amount = cq * up;
      it.current_qty     = pq + qe;
      it.total_previous  = pq * up;
      it.total_current   = it.current_qty * up;
      it.pct             = cq > 0 ? (it.current_qty / cq) * 100 : 0;
      items[idx] = it;
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (idx) => setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const totals = computeTotalsClient(form);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) return toast.error('اختر المشروع');
    if (form.claim_type === 'subcontractor' && !form.subcontractor_id) return toast.error('اختر مقاول الباطن');
    if (!form.items.length) return toast.error('أضف بنود المستخلص');

    // Guard: block negative quantities
    const negativeIdx = form.items.findIndex((i) =>
      Number(i.quantity_executed || 0) < 0 ||
      Number(i.previous_qty || 0) < 0 ||
      Number(i.current_qty || 0) < 0);
    if (negativeIdx >= 0) {
      return toast.error(`البند ${negativeIdx + 1}: القيم السالبة غير مسموحة`);
    }

    if (totals.current_gross <= 0) return toast.error('قيمة المستخلص الحالي يجب أن تكون أكبر من صفر');

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({
          description:       i.description || '',
          unit:              i.unit || 'ls',
          contract_qty:      Number(i.contract_qty || 0),
          unit_price:        Number(i.unit_price || 0),
          contract_amount:   Number(i.contract_amount || 0),
          quantity_executed: Number(i.quantity_executed || 0),
          previous_qty:      Number(i.previous_qty || 0),
          current_qty:       Number(i.current_qty || 0),
          total_previous:    Number(i.total_previous || 0),
          total_current:     Number(i.total_current || 0),
          pct:               Number(i.pct || 0),
        })),
        retention_rate: Number(form.retention_rate || 0),
        advance_recovery_rate: Number(form.advance_recovery_rate || 0),
        advance_recovery_amount: Number(form.advance_recovery_amount || 0),
        vat_rate: Number(form.vat_rate || 0),
        subcontractor_id: form.claim_type === 'subcontractor' ? form.subcontractor_id : null,
        cost_center_id: form.cost_center_id || null,
      };
      if (editingId) {
        await axiosInstance.put(`/progress-claims/${editingId}`, payload);
        toast.success('تم تحديث المستخلص');
      } else {
        await axiosInstance.post('/progress-claims', payload);
        toast.success('تم إنشاء المستخلص. ينتظر الاعتماد.');
      }
      setShowModal(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSubmitting(false);
    }
  };

  const doApprove = async (id) => {
    if (!window.confirm('اعتماد المستخلص؟ سيُنشأ قيد محاسبي تلقائياً.')) return;
    try {
      const res = await axiosInstance.post(`/progress-claims/${id}/approve`);
      toast.success(`اعتُمد ${res.data.journal_no ? `(${res.data.journal_no})` : ''}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const doReject = async (id) => {
    try { await axiosInstance.post(`/progress-claims/${id}/reject`); toast.success('تم الرفض'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const doMarkPaid = async (id) => {
    try { await axiosInstance.post(`/progress-claims/${id}/mark-paid`); toast.success('تم وضع علامة "مدفوع"'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const doDelete = async (id) => {
    if (!window.confirm('حذف المستخلص؟ سيتم عكس القيد المحاسبي.')) return;
    try { await axiosInstance.delete(`/progress-claims/${id}`); toast.success('تم الحذف'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };

  return (
    <div className="space-y-6" data-testid="progress-claims-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileBarChart className="w-7 h-7 text-emerald-600" /> Progress Claims
          </h1>
          <p className="text-sm text-gray-500 mt-1">المستخلصات — احتجاز ضمان + استرداد دفعة + ضريبة 15% (ZATCA)</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button data-testid="filter-client" onClick={() => setFilterType('client')}
              className={`px-3 py-1 text-sm rounded ${filterType === 'client' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>Client</button>
            <button data-testid="filter-subcontractor" onClick={() => setFilterType('subcontractor')}
              className={`px-3 py-1 text-sm rounded ${filterType === 'subcontractor' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>Subcontractor</button>
          </div>
          <button data-testid="new-pc-btn" onClick={openCreate}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Progress Claim
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400">لا توجد مستخلصات بعد. أنشئ أول مستخلص لبدء الفوترة المرحلية.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">#</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Date</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Project</th>
                {filterType === 'subcontractor' && <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500 font-semibold">Subcontractor</th>}
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Gross</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Retention</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Advance Rec.</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">VAT</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Payable</th>
                <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500 font-semibold">Status</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.claim_number}</td>
                  <td className="px-3 py-2 text-sm">{r.claim_date}</td>
                  <td className="px-3 py-2 text-sm">{r.project_name || '—'}</td>
                  {filterType === 'subcontractor' && <td className="px-3 py-2 text-sm">{r.subcontractor_name || '—'}</td>}
                  <td className="px-3 py-2 text-sm text-right font-mono">{fmt(r.current_gross)}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono text-amber-700">{fmt(r.retention_amount)}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono text-orange-600">{fmt(r.advance_recovery_amount)}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono text-gray-600">{fmt(r.vat_amount)}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono font-semibold text-emerald-700">{fmt(r.total_payable)}</td>
                  <td className="px-3 py-2 text-center"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setViewing(r)}
                        data-testid={`view-pc-${r.claim_number}`}
                        className="p-1.5 hover:bg-gray-100 rounded text-gray-600"><Eye className="w-4 h-4" /></button>
                      {r.status === 'pending' && (
                        <button data-testid={`edit-pc-${r.claim_number}`} onClick={() => openEdit(r)}
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600"><Edit2 className="w-4 h-4" /></button>
                      )}
                      {r.status === 'pending' && isAdmin && (
                        <>
                          <button data-testid={`approve-pc-${r.claim_number}`} onClick={() => doApprove(r.id)}
                            className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Approve"><CheckCircle2 className="w-4 h-4" /></button>
                          <button data-testid={`reject-pc-${r.claim_number}`} onClick={() => doReject(r.id)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Reject"><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      {r.status === 'approved' && isAdmin && (
                        <button data-testid={`paid-pc-${r.claim_number}`} onClick={() => doMarkPaid(r.id)}
                          className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Mark Paid"><Banknote className="w-4 h-4" /></button>
                      )}
                      {(r.status === 'pending' || isAdmin) && (
                        <button data-testid={`delete-pc-${r.claim_number}`} onClick={() => doDelete(r.id)}
                          className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ─────────────── Create / Edit Modal ─────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-y-auto" data-testid="pc-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
              <h3 className="font-semibold">{editingId ? 'Edit' : 'New'} Progress Claim</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              {/* Top fields */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Claim Type *</label>
                  <select data-testid="pc-type" value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="client">Client (مستخلص عميل)</option>
                    <option value="subcontractor">Subcontractor (مستخلص مقاول باطن)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Project *</label>
                  <select data-testid="pc-project" value={form.project_id} required onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                    <option value="">— Select —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                {form.claim_type === 'subcontractor' && (
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Subcontractor *</label>
                    <select data-testid="pc-subcontractor" value={form.subcontractor_id} required onChange={(e) => setForm({ ...form, subcontractor_id: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                      <option value="">— Select —</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name_english || s.name_arabic}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Claim Date *</label>
                  <input type="date" data-testid="pc-date" value={form.claim_date} required
                    onChange={(e) => setForm({ ...form, claim_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Claim Period</label>
                  <input type="month" value={form.claim_period} onChange={(e) => setForm({ ...form, claim_period: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
              </div>

              {/* Items table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-700 flex items-center justify-between">
                  <span>BOQ Items / بنود الأعمال</span>
                  <button type="button" data-testid="add-item-btn" onClick={addItem}
                    className="text-emerald-600 hover:text-emerald-700 text-xs flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add item
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Description</th>
                        <th className="px-2 py-1.5 text-right">Contract Qty</th>
                        <th className="px-2 py-1.5 text-right">Quantity Executed</th>
                        <th className="px-2 py-1.5 text-right">Unit Price</th>
                        <th className="px-2 py-1.5 text-right">Contract Amount</th>
                        <th className="px-2 py-1.5 text-right">Previous</th>
                        <th className="px-2 py-1.5 text-right">Total Previous</th>
                        <th className="px-2 py-1.5 text-right">Current</th>
                        <th className="px-2 py-1.5 text-right">Total Current</th>
                        <th className="px-2 py-1.5 text-right">%</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, idx) => {
                        const cq = Number(it.contract_qty || 0);
                        const currentQty = Number(it.current_qty || 0);
                        const overrun = cq > 0 && currentQty > cq;
                        const negative = Number(it.quantity_executed || 0) < 0 || currentQty < 0;
                        return (
                        <tr key={idx}
                            className={`border-t border-gray-100 ${negative ? 'bg-red-50' : overrun ? 'bg-amber-50' : ''}`}
                            data-testid={`pc-item-row-${idx}`}>
                          <td className="px-2 py-1">
                            <input value={it.description}
                              onChange={(e) => updateItem(idx, { description: e.target.value })}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs"
                              placeholder="Item description" />
                            {overrun && (
                              <div className="text-[10px] text-amber-700 mt-0.5"
                                   data-testid={`pc-overrun-warn-${idx}`}>
                                ⚠ الكمية المنفذة تجاوزت كمية العقد
                              </div>
                            )}
                            {negative && (
                              <div className="text-[10px] text-red-700 mt-0.5"
                                   data-testid={`pc-negative-warn-${idx}`}>
                                ✕ القيم السالبة غير مسموحة
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" step="0.01" value={it.contract_qty}
                              onChange={(e) => updateItem(idx, { contract_qty: e.target.value })}
                              className="w-20 border border-gray-200 rounded px-1 py-1 text-xs text-right font-mono"
                              data-testid={`pc-contract-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" step="0.01" value={it.quantity_executed}
                              onChange={(e) => updateItem(idx, { quantity_executed: e.target.value })}
                              className="w-24 border border-gray-200 rounded px-1 py-1 text-xs text-right font-mono"
                              data-testid={`pc-quantity-executed-${idx}`} />
                          </td>
                          <td className="px-2 py-1">
                            <input type="number" step="0.01" value={it.unit_price}
                              onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                              className="w-24 border border-gray-200 rounded px-1 py-1 text-xs text-right font-mono"
                              data-testid={`pc-unit-price-${idx}`} />
                          </td>
                          <td className="px-2 py-1 text-right font-mono text-gray-600"
                              data-testid={`pc-contract-amount-${idx}`}>{fmt(it.contract_amount)}</td>
                          <td className="px-2 py-1">
                            <input type="number" step="0.01" value={it.previous_qty}
                              onChange={(e) => updateItem(idx, { previous_qty: e.target.value })}
                              className="w-20 border border-gray-200 rounded px-1 py-1 text-xs text-right font-mono"
                              data-testid={`pc-previous-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-1 text-right font-mono text-gray-600"
                              data-testid={`pc-total-previous-${idx}`}>{fmt(it.total_previous)}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold"
                              data-testid={`pc-current-qty-${idx}`}>{fmtQty(it.current_qty)}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700"
                              data-testid={`pc-total-current-${idx}`}>{fmt(it.total_current)}</td>
                          <td className={`px-2 py-1 text-right font-mono ${overrun ? 'text-amber-700 font-semibold' : 'text-gray-600'}`}
                              data-testid={`pc-pct-${idx}`}>{fmt(it.pct)}%</td>
                          <td className="px-2 py-1 text-right">
                            {form.items.length > 1 && (
                              <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Settings + Totals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Settings</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-600">Retention %</label>
                      <input type="number" step="0.01" data-testid="pc-retention-rate" value={form.retention_rate}
                        onChange={(e) => setForm({ ...form, retention_rate: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Advance Rec. %</label>
                      <input type="number" step="0.01" data-testid="pc-advance-recovery-rate" value={form.advance_recovery_rate}
                        onChange={(e) => setForm({ ...form, advance_recovery_rate: e.target.value, advance_recovery_amount: 0 })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">VAT %</label>
                      <input type="number" step="0.01" data-testid="pc-vat-rate" value={form.vat_rate}
                        onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Advance Recovery Amount (absolute, overrides %)</label>
                    <input type="number" step="0.01" data-testid="pc-advance-amount" value={form.advance_recovery_amount}
                      onChange={(e) => setForm({ ...form, advance_recovery_amount: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                  </div>
                  {costCenters.length > 0 && (
                    <div>
                      <label className="text-xs text-gray-600">Cost Center</label>
                      <select value={form.cost_center_id} onChange={(e) => setForm({ ...form, cost_center_id: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                        <option value="">— None —</option>
                        {costCenters.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} • {cc.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-600">Notes</label>
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs" rows={2} />
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-1.5">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Computed Totals</div>
                  <div className="flex justify-between text-sm">
                    <span>Cumulative Work Done <span className="text-[10px] text-gray-500">(Σ Total Current)</span></span>
                    <span className="font-mono" data-testid="pc-cumulative-work-done">{fmt(totals.cumulative_work_done)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t pt-1.5">
                    <span>Current Gross <span className="text-[10px] font-normal text-gray-500">(Σ Total Current − Σ Total Previous)</span></span>
                    <span className="font-mono text-emerald-700" data-testid="pc-total-gross">{fmt(totals.current_gross)}</span>
                  </div>
                  <div className="flex justify-between text-sm"><span>Retention (−)</span><span className="font-mono text-amber-700" data-testid="pc-total-retention">{fmt(totals.retention_amount)}</span></div>
                  <div className="flex justify-between text-sm"><span>Advance Recovery (−)</span><span className="font-mono text-orange-600" data-testid="pc-total-advance">{fmt(totals.advance_recovery_amount)}</span></div>
                  <div className="flex justify-between text-sm"><span>VAT (+) <span className="text-[10px] text-gray-500">((Gross − Advance Rec.) × VAT%)</span></span><span className="font-mono text-gray-600" data-testid="pc-total-vat">{fmt(totals.vat_amount)}</span></div>
                  <div className="flex justify-between text-base font-bold border-t pt-1.5"><span>Total Payable</span><span className="font-mono text-emerald-700" data-testid="pc-total-payable">{fmt(totals.total_payable)}</span></div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="pc-submit" disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                  {submitting ? 'Saving…' : (editingId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────────── View Modal ─────────────── */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5" /> {viewing.claim_number} — {viewing.project_name}
              </h3>
              <button onClick={() => setViewing(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Type:</span> {viewing.claim_type}</div>
                <div><span className="text-gray-500">Status:</span> <StatusBadge status={viewing.status} /></div>
                <div><span className="text-gray-500">Date:</span> {viewing.claim_date}</div>
                <div><span className="text-gray-500">Period:</span> {viewing.claim_period || '—'}</div>
                {viewing.journal_no && <div><span className="text-gray-500">Journal:</span> <span className="font-mono">{viewing.journal_no}</span></div>}
              </div>
              <div className="border border-gray-200 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr><th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Cumulative</th>
                      <th className="px-3 py-2 text-right">Previous</th>
                      <th className="px-3 py-2 text-right">Current</th></tr>
                  </thead>
                  <tbody>
                    {viewing.items?.map((it, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-2">{it.description}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(it.cumulative_amount)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-500">{fmt(it.previous_amount)}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(it.current_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 rounded p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Current Gross</span><span className="font-mono">{fmt(viewing.current_gross)}</span></div>
                <div className="flex justify-between"><span>Retention ({viewing.retention_rate}%)</span><span className="font-mono text-amber-700">−{fmt(viewing.retention_amount)}</span></div>
                <div className="flex justify-between"><span>Advance Recovery</span><span className="font-mono text-orange-600">−{fmt(viewing.advance_recovery_amount)}</span></div>
                <div className="flex justify-between"><span>VAT ({viewing.vat_rate}%)</span><span className="font-mono">+{fmt(viewing.vat_amount)}</span></div>
                <div className="flex justify-between border-t pt-1 font-bold"><span>Total Payable</span><span className="font-mono text-emerald-700">{fmt(viewing.total_payable)}</span></div>
              </div>
              {viewing.notes && (
                <div className="text-sm bg-amber-50 border-l-4 border-amber-300 p-3">
                  <div className="text-xs font-semibold text-amber-800 mb-1">Notes</div>
                  <div className="text-gray-700 whitespace-pre-wrap">{viewing.notes}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressClaims;
